/**
 * Invoice Filter Service
 *
 * This module handles the application of filters to invoice data, including:
 *  - Filtering invoices by supplier, status, date range, and amount ranges
 *  - Applying sub-table filters for specific projects, sections, POs, suppliers, or periods
 *  - Aggregating summary statistics (total invoices, amount, suppliers, POs, payment status)
 *  - Supporting dynamic date range calculations (e.g., last 30/90/180/365 days)
 *  - Handling edge cases for invalid or missing filter values
 *
 * Supports flexible filtering for the invoice management module's table and chart displays.
 */
import moment from "moment";
import { toUSD } from "../../../util/currency-formatter";
import { toDecimal } from "../../../util/decimal-utils";
import idx from "idx";
import { getQuarterFromDate } from "./finance-quarter";
import { FILTER_KEYS_VALUES, INVOICE_DATE_FORMAT, INVOICE_FILTER_OPTIONS, INVOICE_GROUPING_VALUES, INVOICE_MONTH_FORMAT, INVOICE_STATUS } from "../../../util/enums/invoice-management";

export const handleApplyFilters = (invoices, filterState, subTable, financeSettings = {}) => {
    const tempInvoices = invoices.filter((invoice) => {
        let matchesSubTable = true;
        if (subTable) {
            if (subTable.type === INVOICE_GROUPING_VALUES.PO) {
                matchesSubTable = subTable.stageId ? invoice.stageId === subTable.id : invoice.projectId === subTable.projectId;
            } else if (subTable.type === INVOICE_GROUPING_VALUES.INVOICES) {
                matchesSubTable = idx(invoice, _ => invoice.PurchaseOrders.id) === subTable.id;
            } else if (subTable.type === INVOICE_GROUPING_VALUES.SUPPLIER_INVOICES) {
                matchesSubTable = String(invoice.PurchaseOrders?.vendorId) === String(subTable.id);
            } else if (subTable.type === INVOICE_GROUPING_VALUES.MONTH_INVOICES) {
                if (subTable.id === FILTER_KEYS_VALUES.EMPTY_ISSUE_DATE) {
                    // Handle empty issue date case
                    const issueDate = moment(invoice.issueDate, INVOICE_DATE_FORMAT, true);
                    matchesSubTable = invoice.issueDate === INVOICE_STATUS.NA || !issueDate.isValid();
                } else {
                    const issueDate = moment(invoice.issueDate, INVOICE_DATE_FORMAT, true);
                    matchesSubTable = issueDate.isValid() && issueDate.format(INVOICE_MONTH_FORMAT) === subTable.id;
                }
            } else if (subTable.type === INVOICE_GROUPING_VALUES.QUARTER_INVOICES) {
                if (subTable.id === FILTER_KEYS_VALUES.EMPTY_ISSUE_DATE) {
                    // Handle empty issue date case
                    const issueDate = moment(invoice.issueDate, INVOICE_DATE_FORMAT, true);
                    matchesSubTable = invoice.issueDate === INVOICE_STATUS.NA || !issueDate.isValid();
                } else {
                    const issueDate = moment(invoice.issueDate, INVOICE_DATE_FORMAT, true);
                    if (issueDate.isValid()) {
                        // Use our finance quarter function instead of moment().quarter()
                        const quarterInfo = getQuarterFromDate(issueDate, financeSettings);
                        matchesSubTable = quarterInfo.quarterKey === subTable.id;
                    } else {
                        matchesSubTable = false;
                    }
                }
            }
        }
        const matchesSearch = !filterState.searchTerm.value ||
            (invoice.invoiceNumber && String(invoice.invoiceNumber).toLowerCase().includes(String(filterState.searchTerm.value).toLowerCase())) ||
            (invoice.supplier && String(invoice.supplier).toLowerCase().includes(String(filterState.searchTerm.value).toLowerCase())) ||
            (invoice.items && invoice.items.some((item) => item.item && String(item.item).toLowerCase().includes(String(filterState.searchTerm.value).toLowerCase())));
        const matchesSupplier = !filterState.supplierId.value?.value ||
            filterState.supplierId.value.value === INVOICE_FILTER_OPTIONS.ALL_SUPPLIERS ||
            invoice.PurchaseOrders?.vendorId === filterState.supplierId.value.value;
        const matchesStatus = !filterState.status.value?.value ||
            filterState.status.value.value === INVOICE_FILTER_OPTIONS.ALL_PAYMENT_STATUS ||
            invoice.status === filterState.status.value.value;
        let matchesDate = true;
        if (filterState.dateRange.value?.value && filterState.dateRange.value.value !== INVOICE_FILTER_OPTIONS.ALL_TIME) {
            let startDate, endDate;
            switch (filterState.dateRange.value.value) {
                case INVOICE_FILTER_OPTIONS.TODAY:
                    startDate = moment().startOf('day');
                    endDate = moment().endOf('day');
                    break;
                case INVOICE_FILTER_OPTIONS.THIS_WEEK:
                    startDate = moment().startOf('week');
                    endDate = moment().endOf('week');
                    break;
                case INVOICE_FILTER_OPTIONS.THIS_MONTH:
                    startDate = moment().startOf('month');
                    endDate = moment().endOf('month');
                    break;
                case INVOICE_FILTER_OPTIONS.LAST_MONTH:
                    startDate = moment().subtract(1, 'month').startOf('month');
                    endDate = moment().subtract(1, 'month').endOf('month');
                    break;
                case INVOICE_FILTER_OPTIONS.THIS_QUARTER:
                    startDate = moment().startOf('quarter');
                    endDate = moment().endOf('quarter');
                    break;
                case INVOICE_FILTER_OPTIONS.LAST_QUARTER:
                    startDate = moment().subtract(1, 'quarter').startOf('quarter');
                    endDate = moment().subtract(1, 'quarter').endOf('quarter');
                    break;
                case INVOICE_FILTER_OPTIONS.THIS_YEAR:
                    startDate = moment().startOf('year');
                    endDate = moment().endOf('year');
                    break;
                case INVOICE_FILTER_OPTIONS.LAST_YEAR:
                    startDate = moment().subtract(1, 'year').startOf('year');
                    endDate = moment().subtract(1, 'year').endOf('year');
                    break;
                case INVOICE_FILTER_OPTIONS.LAST_30_DAYS:
                    startDate = moment().subtract(30, 'days').startOf('day');
                    endDate = moment().endOf('day');
                    break;
                case INVOICE_FILTER_OPTIONS.LAST_90_DAYS:
                    startDate = moment().subtract(90, 'days').startOf('day');
                    endDate = moment().endOf('day');
                    break;
                case INVOICE_FILTER_OPTIONS.CUSTOM:
                    startDate = filterState.startDate?.value && moment(filterState.startDate.value, INVOICE_DATE_FORMAT, true).isValid()
                        ? moment(filterState.startDate.value, INVOICE_DATE_FORMAT).startOf('day')
                        : null;
                    endDate = filterState.endDate?.value && moment(filterState.endDate.value, INVOICE_DATE_FORMAT, true).isValid()
                        ? moment(filterState.endDate.value, INVOICE_DATE_FORMAT).endOf('day')
                        : null;
                    break;
                default:
                    startDate = null;
                    endDate = null;
            }
            if (startDate && endDate && startDate.isValid() && endDate.isValid()) {
                const invoiceDate = moment(invoice.issueDate, INVOICE_DATE_FORMAT, true);
                matchesDate = invoiceDate.isValid() && invoiceDate.isSameOrAfter(startDate, 'day') && invoiceDate.isSameOrBefore(endDate, 'day');
            } else if (filterState.dateRange.value.value === INVOICE_FILTER_OPTIONS.CUSTOM) {
                matchesDate = true; // Allow all invoices if custom dates are invalid
            }
        }

        const minAmountDecimal = filterState.minAmount.value ? toDecimal(filterState.minAmount.value) : null;
        const maxAmountDecimal = filterState.maxAmount.value ? toDecimal(filterState.maxAmount.value) : null;
        const invoiceAmountDecimal = toDecimal(invoice.totalAmount || 0);

        const matchesAmount = (!minAmountDecimal || invoiceAmountDecimal.gte(minAmountDecimal)) &&
            (!maxAmountDecimal || invoiceAmountDecimal.lte(maxAmountDecimal));

        const matchesHierarchy = (!filterState.selectedProjectId?.value || invoice.projectId === filterState.selectedProjectId?.value) &&
            (!filterState.selectedStageId?.value || invoice.stageId === filterState.selectedStageId?.value || `${invoice.stageId}-${invoice.projectId}` === filterState.selectedStageId?.value) &&
            (!filterState.selectedTaskId?.value || invoice.taskId === filterState.selectedTaskId?.value);

        return matchesSearch && matchesSupplier && matchesStatus && matchesDate && matchesAmount && matchesHierarchy && matchesSubTable;
    });

    // Calculate Summary Stats
    const calculatedTotalAmount = tempInvoices.reduce((sum, inv) => {
        const amount = toDecimal(inv.totalAmount || 0);
        return sum.plus(amount);
    }, toDecimal(0));

    const uniqueFilteredSuppliers = new Set(tempInvoices.map((inv) => inv.PurchaseOrders?.vendorId).filter(Boolean));
    const uniquePOs = new Set(tempInvoices.map((inv) => idx(inv, _ => inv.PurchaseOrders.id)).filter(Boolean));
    const currentPaymentStatuses = new Set(tempInvoices.map((inv) => inv.status));
    const paymentStatusSummary = currentPaymentStatuses.size === 1 ? Array.from(currentPaymentStatuses)[0] || INVOICE_STATUS.NA : 'Mixed';
    const invoiceCount = tempInvoices.reduce((sum, inv) => {
        if (inv.status === INVOICE_STATUS.PENDING) {
            return { ...sum, pendingInvoice: sum.pendingInvoice + 1 };
        } else {
            return { ...sum, totalInvoice: sum.totalInvoice + 1 };
        }
    }, { totalInvoice: 0, pendingInvoice: 0 });

    return {
        tempInvoices,
        totalInvoices: invoiceCount.totalInvoice,
        pendingInvoices: invoiceCount.pendingInvoice,
        totalAmount: toUSD(calculatedTotalAmount),
        totalSuppliers: uniqueFilteredSuppliers.size,
        totalPOs: uniquePOs.size,
        paymentStatus: paymentStatusSummary
    };
};